import { useState, useCallback, useRef } from 'react';

// Hebrew responses for the AI Mentor
const hebrewResponses = [
  "יופי! 🎉 אני רואה שהשתמשת בלולאה. נסה עכשיו להוסיף תנאי בתוך הלולאה!",
  "מעולה! הקוד שלך עובד. רוצה לנסות לשפר את הביצועים?",
  "אהבתי את הפתרון! 💡 יש דרך אחרת לעשות את זה - רוצה לראות?",
  "טעות קטנה, אבל אל תדאג! בדוק את השורה שבה יש את הלולאה.",
  "נהדר! עכשיו אתה מבין איך עובדים משתנים ב-Python! 🐍",
  "וואו, התקדמת מהר! בוא ננסה משהו יותר מאתגר?",
  "זה בדיוק מה שחיפשתי! אתה מתכנת אמיתי! ⭐",
  "שאלה טובה! בפייתון, אנחנו משתמשים ב-'if' כדי לבדוק תנאים.",
  "בוא נחשוב על זה יחד - מה קורה כשהלולאה מסתיימת?",
  "מצוין! רוצה שאראה לך טריק מגניב ב-Python? 🚀",
];

export const useChat = () => {
  const [messages, setMessages] = useState([
    {
      id: '1',
      role: 'assistant',
      content: 'שלום! 👋 אני החונך שלך לפייתון. אפשר לשאול אותי כל שאלה על תכנות, או לשתף איתי קוד שכתבת ואעזור לך להבין אותו טוב יותר!',
      timestamp: new Date(),
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messageIdCounter = useRef(2);

  // Send message to AI
  const sendMessage = useCallback(async (content, codeContext = null) => {
    if (!content.trim()) return;

    const userMessage = {
      id: String(messageIdCounter.current++),
      role: 'user',
      content: codeContext ? `${content}\n\n\`\`\`python\n${codeContext}\n\`\`\`` : content,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      // Try to call the real backend API
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          code_context: codeContext,
          history: messages.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          id: String(messageIdCounter.current++),
          role: 'assistant',
          content: data.response,
          timestamp: new Date(),
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        throw new Error('API not available');
      }
    } catch (err) {
      // Fallback to mock response
      console.log('Using mock response (backend not available or API error)');
      
      // Simulate streaming delay
      await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 1200));

      // Select contextual response
      let response;
      if (content.includes('לולאה') || content.includes('loop') || content.includes('for')) {
        response = "לולאות ב-Python מאפשרות לנו לחזור על פעולה מספר פעמים. למשל:\n\n```python\nfor i in range(5):\n    print(i)\n```\n\nזה ידפיס את המספרים 0 עד 4! 🔄 רוצה לנסות?";
      } else if (content.includes('שגיאה') || content.includes('error') || content.includes('לא עובד')) {
        response = "אל תדאג מטעויות - זה חלק מהלמידה! 💪 שתף איתי את הקוד ואת הודעת השגיאה, ונפתור את זה יחד.";
      } else if (content.includes('פונקציה') || content.includes('function') || content.includes('def')) {
        response = "פונקציות הן בלוקים של קוד שאפשר להשתמש בהם שוב ושוב! למשל:\n\n```python\ndef say_hello(name):\n    print(f'שלום {name}!')\n\nsay_hello('דני')\n```\n\nמגניב, לא? 🎯";
      } else if (content.includes('רשימה') || content.includes('list') || content.includes('[')) {
        response = "רשימות ב-Python מאחסנות מספר ערכים! למשל:\n\n```python\nfruits = ['תפוח', 'בננה', 'תפוז']\nfor fruit in fruits:\n    print(fruit)\n```\n\nאפשר להוסיף, למחוק ולגשת לפריטים לפי מיקום! 📝";
      } else {
        response = hebrewResponses[Math.floor(Math.random() * hebrewResponses.length)];
      }

      const assistantMessage = {
        id: String(messageIdCounter.current++),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [messages]);

  // Clear chat history
  const clearChat = useCallback(() => {
    setMessages([
      {
        id: '1',
        role: 'assistant',
        content: 'שלום! 👋 אני החונך שלך לפייתון. במה אוכל לעזור?',
        timestamp: new Date(),
      }
    ]);
    messageIdCounter.current = 2;
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
  };
};
